import { useCallback, useRef } from 'react';
import { useReactiveVar } from '@apollo/client';

import {
  CandidateSmsStateDocument,
  CandidateSmsStateQuery,
  CandidateSmsStateQueryVariables,
  useCandidateSmsPreviewQuery,
  useCandidateSmsStateQuery,
  useWriteCandidateSmsMutation,
  WriteCandidateSmsMutationVariables,
} from 'core/graphql';
import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import { DOFIN_MANAGER_ROLES } from '../Dofinansowanie/useDofinPermissions';
import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { ISmsError, smsCrashErrors, toSmsErrors } from './smsFeedback';
import {
  ISmsLinkDraft,
  isSmsInFlight,
  SMS_STATE_POLL_MS,
  smsLinkInput,
} from './smsModel';
import { useSmsPreviewInput } from './useSmsPreviewInput';
import type {
  IStudentSmsSendInput,
  IStudentSmsSendResult,
  IUseSmsPreview,
} from './useStudentSms';

/**
 * ТЗ 0084 — SMS кандидату дофинансирования из его карточки: зеркало
 * `useTeacherSms`. Можно ли и куда уйдёт, история его SMS, живой
 * предпросмотр и отправка.
 */

/**
 * Кто пишет кандидату — роли, которые ведут дофинансирование
 * (`DOFIN_MANAGER_ROLES` бэкенда). FinanceManager раздел читает, но не
 * пишет — у него панели нет, и запрос не уходит.
 */
export const canSmsCandidateFor = (roles: UserRole[]): boolean =>
  roles.some((role) => DOFIN_MANAGER_ROLES.includes(role));

export const useCanSmsCandidate = (): boolean => {
  const userState = useReactiveVar(userStateVar);
  return canSmsCandidateFor((userState.me?.roles ?? []) as UserRole[]);
};

export const useCandidateSmsState = (candidateId: string, enabled = true) => {
  const canUse = useCanSmsCandidate();
  const skip = !canUse || !enabled || !candidateId;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useCandidateSmsStateQuery({
    variables: { candidateId },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const state = data?.fundingCandidateSms ?? null;
  // Опрос — только пока что-то в пути.
  const inFlight = Boolean(state && isSmsInFlight(state.messages));
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    SMS_STATE_POLL_MS,
    !skip && inFlight,
  );
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  return {
    canUse,
    state,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
  };
};

/** Живой предпросмотр SMS кандидату (как у студента: прошлый до нового). */
export const useCandidateSmsPreview = (
  candidateId: string,
  text: string,
  link: ISmsLinkDraft,
  enabled = true,
): IUseSmsPreview => {
  const canUse = useCanSmsCandidate();
  const { debounced, lagging, hasText } = useSmsPreviewInput(text, link);
  const skip = !canUse || !enabled || !candidateId || !debounced.text;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useCandidateSmsPreviewQuery({
    variables: { candidateId, text: debounced.text, link: debounced.link },
    skip,
    fetchPolicy: 'network-only',
  });
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  if (!hasText) {
    return { preview: null, pending: false, failed: false, reload };
  }
  const current = (data ?? previousData)?.previewFundingCandidateSms ?? null;
  return {
    preview: current,
    pending: lagging || loading,
    failed: Boolean(error) && !current,
    reload,
  };
};

export type ICandidateSmsSendInput = IStudentSmsSendInput;
export type ICandidateSmsSendResult = IStudentSmsSendResult;

/**
 * Отправить SMS кандидату. Ответ несёт состояние целиком — оно пишется в
 * запрос карточки руками (не `refetchQueries` по имени).
 */
export const useCandidateSmsSend = (candidateId: string) => {
  const [sendMut, { loading: sending }] = useWriteCandidateSmsMutation();
  const inFlight = useRef(false);

  const send = useCallback(
    async (input: ICandidateSmsSendInput): Promise<ICandidateSmsSendResult> => {
      if (inFlight.current) return { ok: false, errors: [], status: null };
      inFlight.current = true;
      try {
        const variables: WriteCandidateSmsMutationVariables = {
          candidateId,
          text: input.text.trim(),
          link: smsLinkInput(input.link),
          templateId: input.templateId || null,
          requestKey: input.requestKey,
        };
        const { data } = await sendMut({
          variables,
          update: (cache, { data: answer }) => {
            const state = answer?.sendFundingCandidateSms?.state;
            if (!state) return;
            cache.writeQuery<
              CandidateSmsStateQuery,
              CandidateSmsStateQueryVariables
            >({
              query: CandidateSmsStateDocument,
              variables: { candidateId },
              data: { fundingCandidateSms: state },
            });
          },
        });
        const payload = data?.sendFundingCandidateSms;
        const errors: ISmsError[] = toSmsErrors(payload?.customErrors);
        return {
          ok: Boolean(payload?.success) && !errors.length,
          errors,
          status: payload?.message?.status ?? null,
          body: payload?.message?.body ?? null,
        };
      } catch (caught) {
        return { ok: false, errors: smsCrashErrors(caught), status: null };
      } finally {
        inFlight.current = false;
      }
    },
    [candidateId, sendMut],
  );

  return { send, sending };
};
